import { useMemo, useState } from 'react';
import { required, useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useRunRequest } from '@/hooks';
import _ from 'lodash';
import { uploadToS3 } from '@/services/aws';
import { ValidDocTypes } from '@/services/aws/uploadToS3/enums';
import { UploadToS3Response } from '@/services/aws/uploadToS3/types';
import { setIsLoading } from '@/store/slices/turnAdmin';
import { OneOffCheckRecord } from '@/types/turn-api';
import { TurnResourcesEnum, TurnTargetsEnum } from '@/types/turn-enums';
import { normalizeActions, sanitizeFileName } from './adapters';
import { REQUIRES_PROOF } from './constants';
import { FormFields } from './types';
import { RequestOneOffCheckProps } from '../types';

const useController = ({ checks, onClose, record, worker }: RequestOneOffCheckProps) => {
  const notify = useNotify();
  const dispatch = useDispatch();
  const { handleSubmit, watch } = useFormContext<FormFields>();
  const { runRequest } = useRunRequest();
  const [fileUrl, setFileUrl] = useState<string>(null);

  const selectedAction = watch('action');

  const isUpdating = useMemo(() => !!record?.id, [record?.id]);

  const choices = useMemo(
    () => (isUpdating ? normalizeActions(record?.actions) : checks),
    [isUpdating, record?.actions, checks]
  );

  const getSelectedAction = (actionId: number) => choices?.find((choice) => choice?.id === actionId);

  const proofRequired = useMemo(
    () =>
      isUpdating &&
      REQUIRES_PROOF?.includes(getSelectedAction(selectedAction)?.name as OneOffCheckRecord.Action) &&
      !fileUrl,
    [isUpdating, fileUrl, selectedAction]
  );

  const validateProof = (value: string, allValues: FormFields) => {
    if (!proofRequired) return undefined;
    return required()(value, allValues);
  };

  const onDrop = async (files: File[]) => {
    const file = files?.[0];
    if (!file?.name) return;

    try {
      dispatch(setIsLoading(true));
      const file_name = sanitizeFileName(`${_.snakeCase(record?.name)}_${record?.id}`);

      const s3Response: UploadToS3Response = await uploadToS3({
        acl: 'public-read',
        dir_name: import.meta.env.VITE_AWS_ONE_OFF_CHECKS_DIRNAME,
        doc_type: ValidDocTypes.custom_checks,
        file,
        file_name,
        turn_id: worker?.turn_id,
      });

      setFileUrl(s3Response.location);
    } catch (error) {
      console.error(error);
      notify('Error: Failed to upload file', { type: 'error' });
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleRequest = async (values: FormFields) => {
    const { action } = values;

    await runRequest({
      fetchType: isUpdating ? 'UPDATE' : 'CREATE',
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id: worker?.id,
        target: TurnTargetsEnum.OneOffChecks,
        targetId: isUpdating ? record?.id : action,
        ...(isUpdating && { data: { action: getSelectedAction(action)?.name, report_url: fileUrl } }),
      },
      mode: 'notify',
      notify: {
        onError: { message: `Error: One-Off Check was not ${isUpdating ? 'updated' : 'created'}` },
        onSuccess: { message: `One-Off Check ${isUpdating ? 'updated' : 'created'} successfully`, postAction: onClose },
      },
    });
  };

  return {
    checkName: record?.name,
    choices,
    disabled: proofRequired,
    fileInputOptions: { onDrop },
    fileUrl,
    handleRequest: handleSubmit(handleRequest),
    isUpdating,
    onDrop,
    validateProof,
  };
};

export default useController;
